import { FormEvent, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { api, errText } from '../lib/api';
import BeianFooter from '../components/BeianFooter';
import carpLogo from '../assets/carp-logo.svg';

// 微信扫码登录块（与主站同款）：拉一次性二维码（5 分钟）→ 2s 轮询 → 扫码即登录。
// 走公众号带参二维码 + 同源 /api 轮询，不经 OAuth 重定向——子域可用。
function WxQrLogin({ onSuccess }: { onSuccess: (token: string, user: any) => void }) {
  const [qrUrl, setQrUrl] = useState('');
  const [token, setToken] = useState('');
  const [status, setStatus] = useState<'loading' | 'pending' | 'expired' | 'error'>('loading');
  const [msg, setMsg] = useState('');
  const timer = useRef<ReturnType<typeof setInterval>>();

  const start = async () => {
    setStatus('loading');
    setMsg('');
    try {
      const { data } = await api.get('/wechat/qrlogin/code');
      setQrUrl(data.data.url);
      setToken(data.data.token);
      setStatus('pending');
    } catch (e: any) {
      setStatus('error');
      setMsg(e.response?.data?.error || '获取二维码失败');
    }
  };

  useEffect(() => {
    start();
    return () => clearInterval(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!token || status !== 'pending') return;
    timer.current = setInterval(async () => {
      try {
        const { data } = await api.get('/wechat/qrlogin/status', { params: { token } });
        if (data.data.status === 'ok') {
          clearInterval(timer.current);
          onSuccess(data.data.token, data.data.user);
        } else if (data.data.status === 'expired') {
          clearInterval(timer.current);
          // 过期自动换新码（页面超 5 分钟未扫 / 后端重启清了会话都走这里）——滚动新鲜二维码，无死胡同
          setStatus('expired');
          setTimeout(() => start(), 1200);
        }
      } catch {
        /* 网络抖动继续轮询 */
      }
    }, 2000);
    return () => clearInterval(timer.current);
  }, [token, status, onSuccess]);

  if (status === 'loading') return <div className="text-center text-gray-400 text-sm py-8">正在生成二维码…</div>;
  if (status === 'error') return <div className="text-center text-red-600 text-sm py-4">{msg}</div>;

  return (
    <div className="text-center">
      <div className="relative inline-block">
        <img src={qrUrl} alt="微信扫码登录" className="w-52 h-52 mx-auto border border-gray-200 rounded-lg bg-white" />
        {status === 'expired' && (
          <button
            onClick={start}
            className="absolute inset-0 bg-white/90 rounded-lg text-brand-600 font-medium"
          >
            二维码已过期<br />点击刷新
          </button>
        )}
      </div>
      <p className="text-xs text-gray-500 mt-2">
        {status === 'expired' ? '二维码已过期，正在自动刷新，请稍后扫新码…' : '打开微信扫一扫，关注/识别即可登录（5 分钟内有效）'}
      </p>
    </div>
  );
}

// xyz 登录页：与主站 wenzao.site/login 相同的登录方式（账密 + 微信扫码，默认扫码）。
// 账号在主站注册（邀请码/微信），xyz 不设注册页。
export default function LoginPage() {
  const nav = useNavigate();
  const next = new URLSearchParams(useLocation().search).get('next');
  const login = useAuthStore((s) => s.login);
  const setSession = useAuthStore((s) => s.setSession);
  const loading = useAuthStore((s) => s.loading);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState('');
  const [mode, setMode] = useState<'password' | 'qr'>('qr');

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setErr('');
    try {
      await login(username.trim(), password);
      nav(next && next.startsWith('/') ? next : '/problems');
    } catch (e) {
      setErr(errText(e));
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center px-4 py-10">
      <div className="w-full max-w-sm">
        <a href="https://abc.wenzao.site" title="返回文灶主页"><img src={carpLogo} alt="文灶" className="mx-auto mb-3 h-14 w-auto" /></a>
        <h1 className="mb-2 text-center text-2xl font-bold text-gray-900">文灶 · 数理化 AI 解题</h1>
        <p className="mb-8 text-center text-sm text-gray-500">拍题解题，手写作答批改</p>

        <div className="space-y-4 rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
          <div className="flex -mx-2 border-b border-gray-200">
            <button
              onClick={() => setMode('password')}
              className={`px-4 pb-2 text-sm ${mode === 'password' ? 'border-b-2 border-brand-600 font-medium text-brand-600' : 'text-gray-500'}`}
            >
              账号登录
            </button>
            <button
              onClick={() => setMode('qr')}
              className={`px-4 pb-2 text-sm ${mode === 'qr' ? 'border-b-2 border-brand-600 font-medium text-brand-600' : 'text-gray-500'}`}
            >
              微信扫码
            </button>
          </div>

          {mode === 'qr' ? (
            <WxQrLogin onSuccess={(token, user) => { setSession(token, user); nav(next && next.startsWith('/') ? next : '/problems'); }} />
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">用户名 / 邮箱</label>
                <input
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="username"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-brand-500 focus:outline-none"
                  placeholder="用户名或绑定邮箱"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">密码</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-brand-500 focus:outline-none"
                  placeholder="密码"
                />
              </div>
              {err && <p className="text-sm text-red-600">{err}</p>}
              <button
                type="submit"
                disabled={loading || !username.trim() || !password}
                className="w-full rounded-lg bg-brand-600 py-2.5 font-medium text-white hover:bg-brand-700 disabled:opacity-50"
              >
                {loading ? '登录中…' : '登录'}
              </button>
            </form>
          )}

          {mode === 'password' && (
            <p className="text-center text-xs text-gray-400">
              没有账号？请先在 <a href="https://abc.wenzao.site/register" className="text-brand-600 underline">abc.wenzao.site</a> 注册
            </p>
          )}
        </div>
      </div>
      <footer className="mt-10 w-full">
        <BeianFooter />
      </footer>
    </div>
  );
}
